import { apiFetch, apiFetchBlob, toRaw, useRuntimeConfig } from '#imports'
import { usePath } from './usePath'
import { useAuthStore } from '../stores/authStore'
import type { actionResponse, CmsSettings, PageSettings } from '../types/cmsTypes'
import { useCmsEditStore } from '../stores/cmsEditStore'

export const getBase = (global: boolean = false) => {
  const editStore = useCmsEditStore()
  return editStore.getAll(global)
}

export async function saveCmsData(): Promise<actionResponse> {
  const authstore = useAuthStore()
  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const cms_data = {
    ...getBase()
  }

  const options = {
    method: 'POST',
    body: JSON.stringify(toRaw({ data: cms_data, slug: usePath().value }))
  }
  const url = '/api/update-cms-data'
  return apiFetch(url, options)
}

export async function saveGlobalCmsData(): Promise<actionResponse> {
  const authstore = useAuthStore()
  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const cms_data = {
    ...getBase(true)
  }

  const options = {
    method: 'POST',
    body: JSON.stringify(toRaw({ data: cms_data }))
  }
  const url = '/api/update-global-cms-data'
  return apiFetch(url, options)
}

export async function getCurrentUser(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }
  const url = `/api/get-current-user`

  return apiFetch(url, options)
}

export async function getFiles(): Promise<actionResponse> {
  const authstore = useAuthStore()
  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'GET'
  }
  const url = `/api/get-files-list`

  return apiFetch(url, options)
}

export async function uploadFiles(fileList: File[]): Promise<actionResponse> {
  const authstore = useAuthStore()
  const config = useRuntimeConfig()
  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const chunkSize = 1024 * 1024 * 2

  for (const file of fileList) {
    let start = 0
    let serverFilename = file.name
    while (start < file.size) {
      const chunk = file.slice(start, start + chunkSize)
      const formData = new FormData()
      formData.append('chunk', chunk)
      formData.append('filename', serverFilename)
      formData.append('start', start.toString())

      try {
        const res = await fetch(`${config.public.apiBase}/api/upload-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authstore.token}` },
          body: formData
        })

        if (!res.ok) {
          let message: string

          const contentType = res.headers.get('content-type') ?? ''

          if (contentType.includes('application/json')) {
            const json = await res.json()
            message = json.message ?? json.error ?? `http_${res.status}`
          } else {
            const text = await res.text()
            message = text || `http_${res.status}`
          }

          return { status_ok: false, message }
        }

        const data = await res.json()
        if (start === 0) serverFilename = data.filename ?? file.name
      } catch {
        return { status_ok: false, message: 'network_error' }
      }

      start += chunkSize
    }
  }

  return { status_ok: true }
}

export async function getPagesList(): Promise<actionResponse> {
  const authstore = useAuthStore()
  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'GET'
  }
  const url = `/api/get-pages-list`

  return apiFetch(url, options)
}

export async function getNewsList(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }
  const url = `/api/get-news-list`

  return apiFetch(url, options)
}

export async function getPageSettings(slug: string): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }
  const url = `/api/get-page-settings?slug=${slug}`

  return apiFetch(url, options)
}

export async function savePageSettings(slug: string, settings: PageSettings): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ slug: slug, settings: settings })
  }

  const url = `/api/save-page-settings`

  return apiFetch(url, options)
}

export async function saveCmsSettings(settings: CmsSettings): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  settings.unpublished_changes = true

  const options = {
    method: 'POST',
    body: JSON.stringify({ settings: settings })
  }

  const url = `/api/update-cms-settings`

  return apiFetch(url, options)
}

export async function updatePageSlug(new_slug: string, prev_slug: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ new_slug: new_slug, prev_slug: prev_slug })
  }

  const url = `/api/update-page-slug`

  return apiFetch(url, options)
}

export async function getUsersList(): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'GET'
  }

  const url = `/api/get-users`

  return apiFetch(url, options)
}

export async function getCmsData(slug: string): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }

  const url = `/api/get-cms-data?slug=${slug}`

  return apiFetch(url, options)
}

export async function getGlobalCmsData(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }

  const url = `/api/get-global-cms-data`

  return apiFetch(url, options)
}

export async function getCmsSettings(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }

  const url = `/api/get-cms-settings`

  return apiFetch(url, options)
}

export async function getCmsBackupsList(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }

  const url = `/api/get-cms-backups-list`

  return apiFetch(url, options)
}

export async function getDatabaseBackupsList(): Promise<actionResponse> {
  const options = {
    method: 'GET'
  }

  const url = `/api/get-database-backups-list`

  return apiFetch(url, options)
}

export async function createCmsBackup(): Promise<actionResponse> {
  const options = {
    method: 'POST'
  }

  const url = `/api/create-cms-backup`

  return apiFetch(url, options)
}

export async function createDatabaseBackup(): Promise<actionResponse> {
  const options = {
    method: 'POST'
  }

  const url = `/api/create-database-backup`

  return apiFetch(url, options)
}

export async function restoreCmsBackup(filename: string): Promise<actionResponse> {
  const options = {
    method: 'POST',
    filename: filename
  }

  const url = `/api/restore-cms-backup`

  return apiFetch(url, options)
}

export async function restoreDatabaseBackup(filename: string): Promise<actionResponse> {
  const options = {
    method: 'POST',
    filename: filename
  }

  const url = `/api/restore-database-backup`

  return apiFetch(url, options)
}

export async function downloadDBBackupFile(filename: string): Promise<actionResponse> {
  const options = {
    method: 'GET',
    headers: { Accept: 'application/octet-stream' }
  }

  const url = `/api/download-database-backup?filename=${encodeURIComponent(filename)}`

  return apiFetchBlob(url, options)
}

export async function updateUserEmail(userId: number, email: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ userId: userId, email: email })
  }

  const url = `/api/update-user-email`

  return apiFetch(url, options)
}

export async function updateUserPassword(userId: number, password: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ userId: userId, password: password })
  }

  const url = `/api/update-user-password`

  return apiFetch(url, options)
}

export async function deleteUser(userId: number): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ userId: userId })
  }

  const url = `/api/remove-user`

  return apiFetch(url, options)
}

export async function addUser(password: string, email: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ email: email, password: password })
  }

  const url = `/api/create-user`

  return apiFetch(url, options)
}

export async function addPage(new_page: PageSettings): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ slug: new_page.slug, settings: new_page })
  }

  const url = `/api/create-page`

  return apiFetch(url, options)
}

export async function removePage(slug: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ slug: slug })
  }

  const url = `/api/remove-page`

  return apiFetch(url, options)
}

export async function duplicatePage(slug: string): Promise<actionResponse> {
  const authstore = useAuthStore()

  if (!authstore.token) {
    return { status_ok: false, message: 'nie masz tokena' }
  }

  const options = {
    method: 'POST',
    body: JSON.stringify({ slug: slug })
  }

  const url = `/api/duplicate-page`

  return apiFetch(url, options)
}
